import { Check, ShieldAlert } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  PRESETS,
  CAPABILITY_AREAS,
  CAPABILITY_AREA_LABELS,
  CAPABILITY_TIER_LABELS,
  CapabilityTier,
  PresetKey,
  type UserCapabilities,
  type CapabilityArea,
  resolvePreset,
  matchPreset,
} from '@signalvote/shared';

interface CapabilityEditorProps {
  presetKey: PresetKey | null;
  capabilities: UserCapabilities;
  onChange: (presetKey: PresetKey | null, capabilities: UserCapabilities) => void;
  /** Max tiers the caller can grant per area */
  allowedMaxTiers?: Partial<Record<CapabilityArea, CapabilityTier>>;
  /** Show read-only notice for owners */
  isOwner?: boolean;
}

export function CapabilityEditor({
  presetKey,
  capabilities,
  onChange,
  allowedMaxTiers,
  isOwner = false,
}: CapabilityEditorProps) {
  // Fully controlled: the caller owns both values, so "no preset" IS custom
  // mode and there is nothing to mirror. A local copy could show a change the
  // caller never accepted — one thing on screen, another in the save.
  const isCustom = !presetKey;

  if (isOwner) {
    return (
      <div className="flex items-start gap-2 rounded-md border bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Owners have full access to every area. Only the current campaign owner can change these
          permissions, please reach out to them to request a change or transfer ownership.
        </span>
      </div>
    );
  }

  const handlePresetClick = (key: PresetKey) => {
    onChange(key, resolvePreset(key));
  };

  const handleCustomClick = () => {
    onChange(null, capabilities);
  };

  const handleTierChange = (area: CapabilityArea, tier: CapabilityTier) => {
    const updated = { ...capabilities, [area]: tier };
    onChange(matchPreset(updated), updated);
  };

  const canSelectPreset = (key: PresetKey): boolean => {
    if (!allowedMaxTiers) return true;
    const preset = PRESETS.find((p) => p.key === key);
    if (!preset) return false;
    return CAPABILITY_AREAS.every((area) => {
      const required = preset.capabilities[area] ?? CapabilityTier.None;
      const max = allowedMaxTiers[area] ?? CapabilityTier.Manage;
      return required <= max;
    });
  };

  const maxTierForArea = (area: CapabilityArea): CapabilityTier => {
    if (!allowedMaxTiers) return CapabilityTier.Manage;
    return allowedMaxTiers[area] ?? CapabilityTier.Manage;
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium">Permission Preset</label>
        <div className="grid grid-cols-2 gap-2">
          {PRESETS.map((preset) => {
            const disabled = !canSelectPreset(preset.key);
            const selected = !isCustom && presetKey === preset.key;
            return (
              <button
                key={preset.key}
                type="button"
                disabled={disabled}
                onClick={() => handlePresetClick(preset.key)}
                className={cn(
                  'relative flex flex-col items-start rounded-md border p-3 text-left text-sm transition-colors',
                  selected
                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                    : 'hover:bg-accent',
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                {selected && (
                  <Check className="absolute right-2 top-2 h-4 w-4 text-primary" />
                )}
                <span className="font-medium">{preset.label}</span>
                <span className="text-xs text-muted-foreground mt-0.5">
                  {preset.description}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            onClick={handleCustomClick}
            className={cn(
              'relative flex flex-col items-start rounded-md border p-3 text-left text-sm transition-colors col-span-2',
              isCustom
                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                : 'hover:bg-accent'
            )}
          >
            {isCustom && (
              <Check className="absolute right-2 top-2 h-4 w-4 text-primary" />
            )}
            <span className="font-medium">Custom</span>
            <span className="text-xs text-muted-foreground mt-0.5">
              Set individual permissions per area
            </span>
          </button>
        </div>
      </div>

      {isCustom && (
        <div className="space-y-3 rounded-md border p-3 bg-muted/30">
          <label className="text-sm font-medium">Custom Permissions</label>
          {CAPABILITY_AREAS.map((area) => {
            const max = maxTierForArea(area);
            const current = capabilities[area] ?? CapabilityTier.None;
            return (
              <div key={area} className="flex items-center justify-between gap-4">
                <span className="text-sm">{CAPABILITY_AREA_LABELS[area]}</span>
                <Select
                  value={String(current)}
                  onValueChange={(val) => handleTierChange(area, Number(val) as CapabilityTier)}
                >
                  <SelectTrigger className="w-[120px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[CapabilityTier.None, CapabilityTier.View, CapabilityTier.Do, CapabilityTier.Manage].map(
                      (tier) => (
                        <SelectItem
                          key={tier}
                          value={String(tier)}
                          disabled={tier > max}
                        >
                          {CAPABILITY_TIER_LABELS[tier]}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
